import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { api, configureClient } from '@/api/client';
import type { StaffLoginResult, StaffProfile } from '@/api/types';

import { AuthContext, type AuthState, type AuthStatus } from './context';
import { refreshStaffSession } from './refresh';
import { sessionStore } from './session';

/**
 * The staff session, and the only place it is created or destroyed.
 *
 * Three jobs: restore a session on boot from the stored refresh token, hand the
 * client the token and the refresh callback it needs, and drop everything when
 * the server says the session is over. Nothing in here decides what a role may
 * do — the server does that on every request, and RequireRole only spares the
 * user a screen full of 403s.
 */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [staff, setStaff] = useState<StaffProfile | null>(null);
  const queryClient = useQueryClient();

  /*
   * The client calls back into here from inside a request, which can happen
   * before this effect's state has settled and after the component would have
   * unmounted. A ref keeps those callbacks pointing at live functions without
   * re-registering the client on every render.
   */
  const forgetSession = useCallback(() => {
    sessionStore.clear();
    setStaff(null);
    setStatus('anonymous');
    // Cached admin lists belong to the person who was signed in. Leaving them
    // for the next account would show one driver another driver's round for as
    // long as the stale data renders.
    queryClient.clear();
  }, [queryClient]);

  const forgetRef = useRef(forgetSession);
  forgetRef.current = forgetSession;

  useEffect(() => {
    configureClient({
      getAccessToken: () => sessionStore.getAccessToken(),
      refresh: refreshStaffSession,
      onUnauthorized: () => {
        forgetRef.current();
      },
    });
  }, []);

  /* ------------------------------------------------------------- restore */

  useEffect(() => {
    let cancelled = false;

    async function restore(): Promise<void> {
      // Exchange first. The access token went with the last page, so
      // /staff/auth/me would 401 and trigger the very refresh we are doing.
      const session = await refreshStaffSession();
      if (session === null) {
        sessionStore.clear();
        if (!cancelled) setStatus('anonymous');
        return;
      }

      try {
        const profile = await api.get<StaffProfile>('/staff/auth/me');
        if (cancelled) return;
        setStaff(profile);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        sessionStore.clear();
        setStaff(null);
        setStatus('anonymous');
      }
    }

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  /* -------------------------------------------------- cross-tab sign-out */

  useEffect(() => sessionStore.subscribeToSignOut(() => forgetRef.current()), []);

  /* -------------------------------------------------------------- actions */

  const signIn = useCallback(
    async (email: string, password: string): Promise<StaffProfile> => {
      // Errors are deliberately not caught: Login.tsx renders the API's own
      // message, and 401 'invalidCredentials' and 429 read very differently to
      // the person typing.
      const result = await api.post<StaffLoginResult>(
        '/staff/auth/login',
        { email, password },
        { authenticated: false },
      );
      sessionStore.set(result.session);
      queryClient.clear();
      setStaff(result.staff);
      setStatus('authenticated');
      return result.staff;
    },
    [queryClient],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const refreshToken = sessionStore.getRefreshToken();
    try {
      // Best effort: the server revoking the token is what actually ends the
      // session, but a failed call must not strand someone signed in on a
      // shared machine, so the local state is cleared either way.
      await api.post<void>('/staff/auth/logout', refreshToken === null ? {} : { refreshToken });
    } catch {
      // Already expired, or offline. Nothing left to do here.
    } finally {
      forgetRef.current();
    }
  }, []);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const profile = await api.get<StaffProfile>('/staff/auth/me');
      setStaff(profile);
      setStatus('authenticated');
    } catch {
      forgetRef.current();
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, staff, signIn, signOut, reload }),
    [status, staff, signIn, signOut, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
